import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeft, Cookie, Settings, BarChart2, Shield, Info, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Helmet } from 'react-helmet-async';

const CookiePolicy = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const cookieTypes = [
    {
      icon: Shield,
      name: isRTL ? 'ملفات تعريف الارتباط الضرورية' : 'Essential Cookies',
      description: isRTL
        ? 'هذه الملفات ضرورية لعمل الموقع بشكل صحيح. تتضمن ملفات تعريف الارتباط للمصادقة وأمان الجلسة.'
        : 'These cookies are necessary for the website to function properly. They include authentication and session security cookies.',
      required: true
    },
    {
      icon: BarChart2,
      name: isRTL ? 'ملفات تعريف الارتباط التحليلية' : 'Analytics Cookies',
      description: isRTL
        ? 'تساعدنا هذه الملفات على فهم كيفية استخدام الزوار لموقعنا لتحسين تجربة المستخدم.'
        : 'These help us understand how visitors use our site to improve user experience.',
      required: false
    },
    {
      icon: Settings,
      name: isRTL ? 'ملفات تعريف الارتباط الوظيفية' : 'Functional Cookies',
      description: isRTL
        ? 'تتذكر هذه الملفات تفضيلاتك مثل اللغة والمنطقة الزمنية لتوفير تجربة مخصصة.'
        : 'These remember your preferences like language and timezone to provide a personalized experience.',
      required: false
    }
  ];

  const sections = [
    {
      title: isRTL ? 'ما هي ملفات تعريف الارتباط؟' : 'What Are Cookies?',
      content: isRTL
        ? 'ملفات تعريف الارتباط هي ملفات نصية صغيرة يتم تخزينها على جهازك عند زيارة موقع ويب. تُستخدم على نطاق واسع لجعل المواقع تعمل بكفاءة أكبر وتوفير معلومات لأصحاب الموقع.'
        : 'Cookies are small text files stored on your device when you visit a website. They are widely used to make websites work more efficiently and provide information to site owners.'
    },
    {
      title: isRTL ? 'كيف نستخدم ملفات تعريف الارتباط؟' : 'How Do We Use Cookies?',
      content: isRTL
        ? 'نستخدم ملفات تعريف الارتباط للحفاظ على جلسة تسجيل الدخول الخاصة بك، وتذكر تفضيلاتك، وتحسين أداء موقعنا، وتحليل استخدام الموقع لتحسين خدماتنا.'
        : 'We use cookies to keep you signed in, remember your preferences, improve site performance, and analyze site usage to enhance our services.'
    },
    {
      title: isRTL ? 'إدارة ملفات تعريف الارتباط' : 'Managing Cookies',
      content: isRTL
        ? 'يمكنك التحكم في ملفات تعريف الارتباط وحذفها من خلال إعدادات متصفحك. لاحظ أن تعطيل بعض الملفات قد يؤثر على وظائف الموقع.'
        : 'You can control and delete cookies through your browser settings. Note that disabling certain cookies may affect website functionality.'
    }
  ];

  return (
    <div className={`min-h-screen bg-background ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* SEO Title */}
      <Helmet>
        <title>Cookie Policy | FlowFeed POS</title>
      </Helmet>
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Link to="/">
              <Button variant="ghost" className={`gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <ArrowLeft className="h-4 w-4" />
                {isRTL ? 'العودة للرئيسية' : 'Back to Home'}
              </Button>
            </Link>
            <h1 className="text-xl font-bold">{isRTL ? 'سياسة ملفات تعريف الارتباط' : 'Cookie Policy'}</h1>
            <div className="w-32" />
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/30 mb-4">
            <Cookie className="h-8 w-8 text-amber-600" />
          </div>
          <h1 className="text-3xl font-bold mb-4">{isRTL ? 'سياسة ملفات تعريف الارتباط' : 'Cookie Policy'}</h1>
          <p className="text-muted-foreground">
            {isRTL ? 'آخر تحديث: ديسمبر 2024' : 'Last updated: December 2024'}
          </p>
        </div>

        {/* Info Sections */}
        <div className="space-y-6 mb-8">
          {sections.map((section, index) => (
            <Card key={index}>
              <CardContent className="p-6">
                <div className={`flex items-start gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                    <Info className="h-5 w-5 text-amber-600" />
                  </div>
                  <div className={isRTL ? 'text-right' : ''}>
                    <h3 className="font-semibold text-lg mb-2">{section.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{section.content}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Cookie Types */}
        <h2 className={`text-xl font-semibold mb-4 ${isRTL ? 'text-right' : ''}`}>
          {isRTL ? 'أنواع ملفات تعريف الارتباط التي نستخدمها' : 'Types of Cookies We Use'}
        </h2>
        <div className="space-y-4 mb-8">
          {cookieTypes.map((cookie, index) => (
            <Card key={index}>
              <CardContent className="p-6">
                <div className={`flex items-start gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={`p-2 rounded-lg ${cookie.required ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-muted'}`}>
                    <cookie.icon className={`h-5 w-5 ${cookie.required ? 'text-emerald-600' : 'text-muted-foreground'}`} />
                  </div>
                  <div className={`flex-1 ${isRTL ? 'text-right' : ''}`}>
                    <div className={`flex items-center gap-2 mb-2 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
                      <h3 className="font-semibold">{cookie.name}</h3>
                      <Badge variant={cookie.required ? 'default' : 'secondary'}>
                        {cookie.required 
                          ? (isRTL ? 'مطلوب' : 'Required') 
                          : (isRTL ? 'اختياري' : 'Optional')
                        }
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{cookie.description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Contact */}
        <Card className="bg-muted/50">
          <CardContent className="p-6 text-center">
            <Mail className="h-8 w-8 mx-auto mb-4 text-muted-foreground" />
            <h3 className="font-semibold mb-2">{isRTL ? 'أسئلة حول ملفات تعريف الارتباط؟' : 'Questions about Cookies?'}</h3>
            <p className="text-muted-foreground mb-4">
              {isRTL ? 'تواصل معنا على' : 'Contact us at'}
            </p>
            <a href="mailto:feed@maxefeed.com" className="text-primary hover:underline font-medium">
              feed@maxefeed.com
            </a>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default CookiePolicy;
